import { BIRouterName } from '@/constant';
import { RouterName } from '@/constant/data';
import { StorageName } from '@/constant/enum';
import { jumpLoginPage } from '@/modules/common/utils';
import { history } from '@umijs/max';
import { message } from 'antd';
import axios, { AxiosRequestConfig } from 'axios';
import { merge } from 'lodash-es';
import { type Response } from './types';

/** 忽略重新需要校验签名的路由 */
const ignoreRoutes = [RouterName.Login, RouterName.AioLogin, BIRouterName.BILogin];
/** 权限失效编码 */
const authCodes = [101001, 101002, 101003, 101004, 101005, 101006];

// 请求实例
const http = axios.create({
  baseURL: '/api',
  validateStatus: (status) => status === 200,
  timeout: 20000,
  timeoutErrorMessage: '请求超时',
});

/**
 * 请求拦截器
 */
http.interceptors.request.use((request) => {
  const session_key = localStorage.getItem(StorageName.SessionKey);
  const serviceDomain = localStorage.getItem(StorageName.ServiceDomain);
  if (serviceDomain) {
    request.baseURL = serviceDomain + '/api';
  }
  const ignore = ignoreRoutes.includes(history.location.pathname);
  /** 获取标识头 */
  const prefix = location.host.split('.')?.[0];
  if (!ignore) {
    request.headers['token'] = session_key;
    if (!session_key) {
      message.error('登录失效，请重新登录。');
      history.replace(RouterName.Login);
    }
  }
  if (prefix) {
    request.headers['tenantPrefix'] = prefix;
  }
  return request;
});

/**
 * 响应拦截器
 */
http.interceptors.response.use(
  (response) => {
    const data = response?.data;
    // 授权失败
    if (authCodes.includes(data?.code)) {
      /** 跳转到登录 */
      jumpLoginPage();
      return;
    }
    return response;
  },
  () => {
    message.error('服务异常，请稍候重试或联系客服');
  },
);

/**
 * 公共请求
 */
export const request = async <S = any>(url: string, config?: AxiosRequestConfig): Promise<S> => {
  const response = await http.request<Response<S>>({
    url,
    ...config,
  });

  const data = response?.data;
  if (data?.code !== 200) {
    message.error(data?.msg || '服务异常，请稍候重试或联系客服');
    return Promise.reject(data?.data);
  }
  return Promise.resolve(data?.data);
};

/**
 * 请求文件,
 * TODO 类型先写any
 */
export const fileRequest = (url: string, config?: AxiosRequestConfig) => {
  return http
    .request<Blob>({
      url,
      responseType: 'blob',
      ...config,
    })
    .then((resp) => resp);
};

/**
 * 上传文件,
 * TODO: 拦截器处理then
 */
export const uploadFileRequest = <T = any>(
  url: string,
  data: Record<string, string | Blob | File | (Blob | File)[]>,
  config?: AxiosRequestConfig,
) => {
  const formData = new window.FormData();
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        formData.append(key, item);
      });
    } else {
      formData.append(key, value);
    }
  });

  return http
    .request<Response<T>>(
      merge(
        {
          url,
          method: 'POST',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          data: formData,
        },
        config,
      ),
    )
    .then((resp) => {
      const data = resp.data;
      if (data?.code !== 200) {
        message.error(data?.msg || '服务异常，请稍候重试或联系客服');
        return Promise.reject(data?.data);
      }
      return Promise.resolve(data?.data);
    });
};
